import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionPanel,
  Box,
  Button,
  Heading,
  Menu,
  Spinner,
  TableCell,
  TableRow,
  Text,
  Tip,
} from "grommet";
import {
  FormNextLink,
  LinkNext,
  Trash,
  Clock,
  Down,
  Up,
  StatusWarning,
  Refresh,
} from "grommet-icons";
import moment from "moment-timezone";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { presentAlertPrompt } from "@shared/Alert";
import { PasscodeService, EKeyService } from "@services/index";
import { useTranslation } from "react-i18next";

import "./AccessList.scss";
import {
  schlageAccessState,
  isInstalledSchlageAccess,
} from "../../../../common/schlageAccessState";

const accessRevision = (access) =>
  JSON.stringify([
    access.status,
    access.commandId,
    access.pendingOperation,
    access.commandError,
    access.revocationRequested,
    access.updatedAt,
  ]);

function SchlagePasscodeValue({ access, state, onRefresh, refreshError }) {
  const { t } = useTranslation();
  const [timedOut, setTimedOut] = useState(false);
  const pending = ["pending", "deleting"].includes(state);
  useEffect(() => {
    setTimedOut(false);
    if (!pending) return undefined;
    const timer = setTimeout(() => setTimedOut(true), 60000);
    return () => clearTimeout(timer);
  }, [pending, state, access.commandId, access.issuanceAttemptId]);

  if (pending && !timedOut) {
    return (
      <Box align="center" justify="center" width={{ min: "48px" }}>
        <Spinner
          size="small"
          role="status"
          aria-label={t(`accessList.schlage.${state}`)}
        />
      </Box>
    );
  }
  const feedback =
    timedOut && pending
      ? state === "deleting"
        ? "removalUnconfirmed"
        : "unconfirmed"
      : state === "active"
        ? null
        : state;
  const uncertain = ["unconfirmed", "removalUnconfirmed"].includes(feedback);
  return (
    <Box align="center" gap="xsmall">
      {isInstalledSchlageAccess(state) && (
        <Box
          border={{ size: "small", color: "text" }}
          round
          pad={{ horizontal: "6px" }}
          align="center"
        >
          <Text>{access.keyboardPwd}</Text>
        </Box>
      )}
      {feedback && (
        <Box direction="row" align="center" gap="xsmall">
          <Tip
            content={t(
              refreshError && uncertain
                ? "accessList.schlage.refreshError"
                : `accessList.schlage.${feedback}Hint`,
            )}
          >
            <Text
              size="small"
              color={uncertain ? "text" : "status-error"}
              role="status"
            >
              {t(
                `accessList.schlage.${feedback}${feedback === "failed" ? "" : "Short"}`,
              )}
            </Text>
          </Tip>
          {uncertain && onRefresh && (
            <Button
              icon={<Refresh size="small" />}
              aria-label={t("accessList.schlage.refresh")}
              onClick={() => onRefresh()}
              plain
            />
          )}
        </Box>
      )}
    </Box>
  );
}

const AccessList = ({
  title,
  keys,
  bookingId,
  hotel,
  onRefresh,
  collapsing,
  refreshError,
  withGuestName,
  otherKeys, // if keys are passcodes, otherKeys are ekeys and vice versa
}) => {
  const { t } = useTranslation();
  const [deletions, setDeletions] = useState({});
  const deletionRequests = useRef(new Set());
  useEffect(() => {
    setDeletions((previous) => {
      const next = { ...previous };
      let changed = false;
      Object.entries(previous).forEach(([id, deletion]) => {
        const access = keys?.find((item) => String(item._id) === id);
        if (
          deletion.phase === "pending" &&
          (!access || accessRevision(access) !== deletion.revision)
        ) {
          delete next[id];
          deletionRequests.current.delete(id);
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [keys, deletions]);
  const handleDeleteAccess = useCallback(
    (access) => {
      presentAlertPrompt({
        title: t("accessList.deleteConfirmTitle"),
        message: t("accessList.deleteConfirmMessage"),
        buttonOKText: t("accessList.deleteConfirmButton"),
        buttonOKColor: "status-critical",
        onOK: async () => {
          const id = String(access._id);
          if (deletionRequests.current.has(id)) return;
          deletionRequests.current.add(id);
          setDeletions((previous) => ({
            ...previous,
            [id]: { phase: "request" },
          }));
          let awaitingConfirmation = false;
          const isSchlage = !!schlageAccessState(access, hotel);
          if (!isSchlage) presentLoading();
          try {
            let response;
            if (access.keyboardPwd) {
              response = await PasscodeService.delete(bookingId, access._id);
              // find the matching ekey and delete it too
              const matchingEKey = otherKeys?.find(
                (k) =>
                  k.door._id === access.door._id &&
                  k.startDate === access.startDate &&
                  k.endDate === access.endDate,
              );
              if (matchingEKey) {
                await EKeyService.delete(matchingEKey._id);
              }
            } else {
              response = await EKeyService.delete(access._id);
              // find the matching passcode and delete it too
              const matchingPasscode = otherKeys?.find(
                (k) =>
                  k.door._id === access.door._id &&
                  k.startDate === access.startDate &&
                  k.endDate === access.endDate,
              );
              if (matchingPasscode) {
                await PasscodeService.delete(bookingId, matchingPasscode._id);
              }
            }
            awaitingConfirmation =
              response.data?.status === "pending" && isSchlage;
            if (awaitingConfirmation) {
              setDeletions((previous) => ({
                ...previous,
                [id]: {
                  phase: "pending",
                  revision: accessRevision(access),
                },
              }));
            }
            if (!isSchlage) dismissLoading();
            if (!awaitingConfirmation) {
              presentToastSuccess(
                response.data?.message || t("accessList.deleted"),
              );
            }
            await onRefresh();
          } catch (error) {
            if (!isSchlage) dismissLoading();
            presentToastErrorContent(error);
            if (isSchlage && onRefresh) {
              // The API can persist an uncertain removal before returning an
              // error. Keep the local guard until its saved state is reread.
              try {
                await onRefresh();
              } catch {
                /* Keep the original error. */
              }
            }
          } finally {
            if (!awaitingConfirmation) {
              deletionRequests.current.delete(id);
              setDeletions((previous) => {
                const next = { ...previous };
                delete next[id];
                return next;
              });
            }
          }
        },
      });
    },
    [bookingId, onRefresh, otherKeys, hotel, t],
  );

  const handleGoToBooking = (id) => {
    window.location.href = id;
  };

  const table = useMemo(
    () => (
      <table
        className={
          keys?.some((key) => schlageAccessState(key, hotel))
            ? "schlage-access-table"
            : undefined
        }
        style={{ width: "fit-content" }}
      >
        <tbody>
          {keys.map((key) => {
            const lifecycle = schlageAccessState(key, hotel);
            const deleting = !!deletions[String(key._id)];
            const state = lifecycle && deleting ? "deleting" : lifecycle;
            return (
              <TableRow key={key._id} align="center">
                <TableCell
                  className="access-room"
                  alignContent="center"
                  direction="row"
                  gap="small"
                >
                  {moment().isAfter(key.endDate) && (
                    <Tip
                      content={
                        <Box>
                          <Text>{t("accessList.expired")}</Text>
                          <Text>{t("accessList.expiredHint")}</Text>
                        </Box>
                      }
                    >
                      <StatusWarning color="status-warning" size="20px" />
                    </Tip>
                  )}
                  <Text>
                    {key.door?.type === "area"
                      ? key.door?.name
                      : t("accessList.roomName", { room: key.door?.name })}
                  </Text>
                  {withGuestName && key.bookings?.[0] && (
                    <Text size="small" color="text-weak">
                      {` (${key.bookings[0].firstname} ${key.bookings[0].lastname})`}
                    </Text>
                  )}
                </TableCell>
                {key.keyboardPwd && (
                  <TableCell className="access-pin">
                    {state ? (
                      <SchlagePasscodeValue
                        access={key}
                        state={state}
                        onRefresh={onRefresh}
                        refreshError={refreshError}
                      />
                    ) : (
                      <Box
                        border={{ size: "small", color: "text" }}
                        round
                        pad={{ horizontal: "6px" }}
                        align="center"
                      >
                        <Text>{key.keyboardPwd}</Text>
                      </Box>
                    )}
                  </TableCell>
                )}
                <TableCell
                  className="access-dates"
                  align="center"
                  direction="row"
                >
                  <Text size="xsmall" color="status-ok">
                    {moment
                      .tz(key.startDate, hotel.timezone)
                      .format("MMM D LT")}
                  </Text>
                  <FormNextLink />
                  <Text
                    size="xsmall"
                    color="status-error"
                    margin={{ right: "8px" }}
                  >
                    {moment.tz(key.endDate, hotel.timezone).format("MMM D LT")}
                  </Text>
                  {key.createdAt && (
                    <Tip
                      content={t("accessList.createdOn", {
                        date: moment
                          .tz(key.createdAt, hotel?.timezone)
                          .format("lll"),
                      })}
                    >
                      <Clock size="small" className="no-print" />
                    </Tip>
                  )}
                </TableCell>

                <TableCell
                  gap="small"
                  direction="row"
                  className="no-print access-actions"
                >
                  {(!key.bookings ||
                    key.bookings.some((x) => x._id === bookingId)) && (
                    <Tip
                      content={t("accessList.removeThisAccess")}
                      dropProps={{ align: { left: "right" } }}
                      margin={{ bottom: "2px" }}
                    >
                      <Button
                        icon={<Trash size="small" />}
                        aria-label={t("accessList.removeThisAccess")}
                        disabled={
                          deleting ||
                          ["deleting", "removalUnconfirmed"].includes(state)
                        }
                        onClick={() => {
                          handleDeleteAccess(key);
                        }}
                        primary
                        color="status-error"
                        style={{ padding: "5px" }}
                      />
                    </Tip>
                  )}
                  {key.bookings?.some((x) => x._id !== bookingId) &&
                    (key.bookings.filter((x) => x._id !== bookingId).length >
                    1 ? (
                      <Tip
                        content={t("accessList.multipleReservations")}
                        dropProps={{ align: { left: "right" } }}
                        margin={{ bottom: "2px" }}
                      >
                        <Menu
                          label={
                            <Button
                              icon={<LinkNext size="small" />}
                              primary
                              color="blue-1"
                              style={{ padding: "5px" }}
                            />
                          }
                          items={key.bookings
                            .filter((x) => x._id !== bookingId)
                            .map((booking) => ({
                              label: t("accessList.goToBooking", {
                                bookingRef: booking.bookingRef,
                              }),
                              onClick: () => handleGoToBooking(booking._id),
                            }))}
                          className="access-menu"
                        />
                      </Tip>
                    ) : (
                      key.bookings[0]._id !== bookingId && (
                        <Tip
                          content={t("accessList.goToBooking", {
                            bookingRef: key.bookings[0].bookingRef,
                          })}
                          dropProps={{ align: { left: "right" } }}
                        >
                          <Button
                            icon={<LinkNext size="small" />}
                            onClick={() => {
                              handleGoToBooking(key.bookings[0]._id);
                            }}
                            primary
                            color="blue-1"
                            style={{ padding: "5px" }}
                          />
                        </Tip>
                      )
                    ))}
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </table>
    ),
    [
      keys,
      withGuestName,
      hotel,
      bookingId,
      handleDeleteAccess,
      deletions,
      onRefresh,
      refreshError,
      t,
    ],
  );

  const header = useMemo(
    () => (
      <Heading level={4} margin="none">
        {title}
      </Heading>
    ),
    [title],
  );

  const [showHideIcon, setShowHideIcon] = React.useState(
    <Down color="brand" />,
  );

  const handleOnActive = (indexes) => {
    setShowHideIcon(
      indexes.includes(0) ? <Up color="brand" /> : <Down color="brand" />,
    );
  };

  return (
    <>
      {keys?.length > 0 ? (
        !collapsing ? (
          <>
            {header} {table}
          </>
        ) : (
          <Accordion onActive={handleOnActive}>
            <AccordionPanel
              header={
                <Box direction="row" gap="small">
                  {header} {showHideIcon}
                </Box>
              }
            >
              {table}
            </AccordionPanel>
          </Accordion>
        )
      ) : (
        <>
          {header}
          <Text>No {title}</Text>
        </>
      )}
    </>
  );
};

export default AccessList;
